import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { ASSETS } from "../data/entrepminds";
import { useCalendly } from "../lib/CalendlyContext";

const goTo = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
  }
};

const Hero = () => {
  const calendly = useCalendly();
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-32 pb-24 overflow-hidden em-grain"
    >
      {/* radial gold ambience */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.18)_0%,transparent_60%)]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.08)_0%,transparent_60%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <div className="section-eyebrow mb-6 inline-flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#d4af37] shadow-[0_0_10px_rgba(184,144,31,0.55)]" />
            Management Consultancy · Dubai
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-[4.5rem] xl:text-[5.25rem] font-semibold leading-[0.95] tracking-[-0.03em]">
            AI-Powered
            <br />
            <span className="gold-text">Business</span>
            <br />
            <span className="gold-text">Transformation</span>
            <br />
            for the AI Era.
          </h1>

          <p className="mt-8 max-w-xl text-[#6b6356] text-lg leading-relaxed">
            We help businesses{" "}
            <span className="text-[#0a0a0a]">automate workflows, fix inefficiencies, build brands, and scale growth</span>{" "}
            by combining strategy, AI technology, and marketing into one system.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => calendly?.open()}
              className="btn-gold"
            >
              <Calendar size={18} />
              Book a Diagnostic
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => goTo("services")}
              className="btn-ghost"
            >
              Explore GrowthOS
              <ArrowRight size={18} />
            </button>
          </div>

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
            {[
              ["Diagnose", "Find the leaks"],
              ["Optimize", "Strengthen the core"],
              ["Automate", "Run 24/7"],
              ["Scale", "Compound growth"],
            ].map(([t, s]) => (
              <div key={t} className="border-l border-[#d4af37]/45 pl-4">
                <div className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#8a6a10]">
                  {t}
                </div>
                <div className="text-xs text-[#6b6356] mt-1">{s}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#d4af37]/45">
            <img
              src={ASSETS.heroAbstract}
              alt="Entrepminds business consultant in Dubai"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#faf7ee] via-transparent to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#faf7ee]/15 via-transparent to-[#d4af37]/10" />

            <div className="absolute bottom-6 left-6 right-6">
              <div className="em-card p-5 bg-white/90 backdrop-blur-md">
                <div className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#8a6a10]">
                  GrowthOS™ · v1.0
                </div>
                <div className="mt-2 text-[#0a0a0a] font-display text-lg">
                  One operating system. Five stages. Infinite leverage.
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
